import { useCallback } from "react";
import { useAppSelector } from "../../redux/hook";
import { SolicitorTypeEnum } from "../../models/enum/solicitor-type.enum";
import { P2Accordion, P2TextLabel, P2Checkbox } from "react-bootstrap-common-script";
import HdbSolicitorTNC from "../../components/HOC/hdb-solicitor-form/hdb-solicitor-tnc";
import Separator from "../../components/separator/separator";
import ReviewPrivateSolicitor from "../application-coo-stage-b/review-endorse/review-private-solicitor";

interface ReviewSolicitorProps {
  foundCurrent: boolean;
  foundProposed: boolean;
}

function ReviewSolicitor({ foundCurrent, foundProposed } : ReviewSolicitorProps) {
  const applicationStep3FormData = useAppSelector((state) => state.app.ApplicationStep3Slice.formData);

  const privateSolicitorComponent = (isConveyancingSameSolicitor: boolean, isDirectConveyancing: boolean, ownerType: "current" | "proposed") => {
    if (isDirectConveyancing) {
      return (
        <div style={{ marginTop: 32 }}>
          <P2TextLabel bold="500" color={"#333"} label={`Legal representation for ${ownerType} owners' conveyancing`} />
          <ReviewPrivateSolicitor showSameSolicitorCheckbox={false} private_solicitor={applicationStep3FormData[ownerType].conveyancing_private_solicitor} />
        </div>
      );
    }

    const loanTitle = ownerType === "current" ? "discharge of current loan" : "new mortgage loan";
    return (
      <div>
        <div style={{ marginTop: 32 }}>
          <P2TextLabel bold="500" color={"#333"} label={`Legal representation for ${ownerType} owners' ${loanTitle}`} />
          <ReviewPrivateSolicitor showSameSolicitorCheckbox={isConveyancingSameSolicitor ? true : false} private_solicitor={applicationStep3FormData[ownerType].private_solicitor} />
        </div>
        {!isConveyancingSameSolicitor && (
          <div style={{ marginTop: 32 }}>
            <P2TextLabel bold="500" color={"#333"} label={`Legal representation for ${ownerType} owners' conveyancing`} />
            <ReviewPrivateSolicitor showSameSolicitorCheckbox={false} private_solicitor={applicationStep3FormData[ownerType].conveyancing_private_solicitor} />
          </div>
        )}
      </div>
    );
  };

  const hdbSolicitorComponent = (isConveyancingSameSolicitor: boolean, isDirectConveyancing: boolean, ownerType: "current" | "proposed") => {
    const title = `Legal representation for ${ownerType} owners' ${isDirectConveyancing ? "conveyancing" : ownerType === "current" ? "discharge of current loan" : "new mortgage loan"}`;
    return (
      <div style={{ marginTop: 32 }}>
        <P2TextLabel bold="500" color={"#333"} label={title} />
        <P2Accordion defaultOpen={true} title={<P2TextLabel bold="700" label={"HDB"} />}>
          <>
            <HdbSolicitorTNC />

            <div className="mt-4">
              <P2Checkbox variant="disabled" isActive={true} description={<P2TextLabel required label={<span className="m-0">I have read the above Terms & Conditions and agree for HDB to represent me.</span>} />} />
            </div>
            {!isDirectConveyancing && isConveyancingSameSolicitor && (
              <>
                <Separator style={{ margin: "24px 0" }} />
                <div>
                  <P2Checkbox
                    variant="disabled"
                    isActive={true}
                    description={
                      <P2TextLabel
                        tooltipContent={
                          <span
                            style={{
                              color: "#212529",
                              lineHeight: "1.5rem",
                              fontSize: "1rem",
                            }}
                          >
                            You would be required to engage a lawyer for the following,
                            <div>1) Discharge of current mortgage loan</div>
                            <div>2) Obtaining fresh mortgage loan</div>
                            <div>3) Conveyancing for the ownership change (current owners)</div>
                            <div>4) Conveyancing for the ownership change (proposed owners)</div>
                            <br />
                            <div>The change in flat ownership is a conveyancing process. As a recommendation, having the same solicitor acting for the process would expedite and reduce the liaising with multiple parties.</div>
                          </span>
                        }
                        hasTooltip
                        label={<span className="m-0">Conveyancing process is represented by the same lawyer as above.</span>}
                      />
                    }
                  />
                </div>
              </>
            )}
          </>
        </P2Accordion>

        {!isDirectConveyancing && !isConveyancingSameSolicitor && (
          <div style={{ marginTop: 32 }}>
            <P2TextLabel bold="500" color={"#333"} label={`Legal representation for ${ownerType} owners' conveyancing`} />
            <ReviewPrivateSolicitor showSameSolicitorCheckbox={false} private_solicitor={ownerType === "current" ? applicationStep3FormData.current.conveyancing_private_solicitor : applicationStep3FormData.proposed.conveyancing_private_solicitor} />
          </div>
        )}
      </div>
    );
  };

  const solicitorComponent = useCallback(
    (ownerType: "current" | "proposed") => {
      const solicitorType = applicationStep3FormData[`${ownerType}_solicitor_type`];
      if (solicitorType === SolicitorTypeEnum.conveyancing_hdb_solicitor || solicitorType === SolicitorTypeEnum.hdb_solicitor) {
        return hdbSolicitorComponent(applicationStep3FormData[ownerType].hdb_solicitor.represented_same_solicitor, solicitorType === SolicitorTypeEnum.conveyancing_hdb_solicitor, ownerType);
      } else {
        return privateSolicitorComponent(applicationStep3FormData[ownerType].private_solicitor.represented_same_solicitor, solicitorType === SolicitorTypeEnum.conveyancing_private_solicitor, ownerType);
      }
    },
    [applicationStep3FormData]
  );

  return (
    <>
      {foundCurrent && solicitorComponent("current")}
      {applicationStep3FormData.proposed_solicitor_type !== undefined && foundProposed && solicitorComponent("proposed")}
    </>
  );
}

export default ReviewSolicitor;
